import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { api } from "@/lib/api";

const EVENT_TYPES = ["Birthday", "Corporate", "Wedding / Engagement", "Cocktail Reception", "Anniversary", "Other"];
const FOOD_PACKAGES = [
  { id: "canapes", label: "Canapés Selection", desc: "Six passed bites per guest" },
  { id: "tasting", label: "Chef's Tasting", desc: "Five-course seated menu" },
  { id: "stations", label: "Live Stations", desc: "Carving, raw bar & dessert stations" },
  { id: "none", label: "Drinks Only", desc: "No food service" },
];
const DRINK_PACKAGES = [
  { id: "classic", label: "Classic Bar", desc: "House wines, beers & well spirits" },
  { id: "premium", label: "Premium Cocktail", desc: "Signature cocktails & curated wines" },
  { id: "reserve", label: "Reserve List", desc: "Vintage spirits & rare bottles" },
  { id: "cash", label: "Cash Bar", desc: "Guests purchase individually" },
];

const initialState = {
  full_name: "",
  email: "",
  phone: "",
  event_type: "",
  attendees: "",
  food_package: "",
  drinks_package: "",
  event_date: null,
  event_time: "",
  special_requests: "",
};

export default function BookingDialog({ open, onOpenChange }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const reset = () => { setData(initialState); setStep(0); setDone(false); };

  const canNext = () => {
    if (step === 0) return data.event_type && data.attendees && Number(data.attendees) > 0;
    if (step === 1) return data.food_package && data.drinks_package;
    if (step === 2) return data.event_date && data.event_time;
    if (step === 3) return data.full_name.trim() && data.email.trim() && data.phone.trim();
    return false;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        ...data,
        attendees: Number(data.attendees),
        event_date: data.event_date ? format(data.event_date, "yyyy-MM-dd") : "",
      });
      setDone(true);
      toast.success("Your enquiry has been received.");
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const steps = ["Event", "Packages", "Date & Time", "Your Details"];

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setTimeout(reset, 300); }}>
      <DialogContent
        data-testid="booking-dialog"
        className="bg-neutral-950 border border-neutral-800 text-white max-w-2xl p-0 rounded-none overflow-hidden"
      >
        <div className="p-8 md:p-10">
          <DialogHeader className="space-y-3 text-left">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">Private Events</p>
            <DialogTitle className="font-serif font-light text-3xl md:text-4xl tracking-tight">
              {done ? "Thank you" : "Plan your evening"}
            </DialogTitle>
            <DialogDescription className="text-neutral-400 text-sm leading-relaxed">
              {done
                ? "We'll be in touch within 24 hours to confirm your reservation."
                : "Tell us about your event — we'll tailor the room and the lineup around it."}
            </DialogDescription>
          </DialogHeader>

          {!done && (
            <div className="mt-8 flex items-center gap-2" data-testid="booking-stepper">
              {steps.map((label, i) => (
                <div key={label} className="flex-1">
                  <div className={`h-px ${i <= step ? "bg-[#D4AF37]" : "bg-neutral-800"}`} />
                  <div className={`mt-2 text-[10px] tracking-[0.25em] uppercase ${i === step ? "text-[#D4AF37]" : "text-neutral-600"}`}>{label}</div>
                </div>
              ))}
            </div>
          )}

          {!done && (
            <div className="mt-8 space-y-6 min-h-[280px]">
              {step === 0 && (
                <div className="space-y-6" data-testid="step-event">
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-neutral-400">Event type</Label>
                    <Select value={data.event_type} onValueChange={(v) => update("event_type", v)}>
                      <SelectTrigger
                        data-testid="event-type-trigger"
                        className="mt-3 bg-transparent border-0 border-b border-neutral-800 rounded-none px-0 py-3 text-white focus:ring-0 focus:border-[#D4AF37] h-auto"
                      >
                        <SelectValue placeholder="Select an event" />
                      </SelectTrigger>
                      <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                        {EVENT_TYPES.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`event-type-${t}`} className="focus:bg-neutral-900 focus:text-[#D4AF37]">{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-neutral-400">Attendees</Label>
                    <Input
                      type="number"
                      min={1}
                      data-testid="attendees-input"
                      value={data.attendees}
                      onChange={(e) => update("attendees", e.target.value)}
                      placeholder="e.g. 40"
                      className="mt-3 bg-transparent border-0 border-b border-neutral-800 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-[#D4AF37] placeholder:text-neutral-600 h-auto"
                    />
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-8" data-testid="step-packages">
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-neutral-400 mb-3 block">Food package</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {FOOD_PACKAGES.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => update("food_package", p.label)}
                          data-testid={`food-pkg-${p.id}`}
                          className={`text-left p-4 border transition-colors ${
                            data.food_package === p.label
                              ? "border-[#D4AF37] bg-[#D4AF37]/5"
                              : "border-neutral-800 hover:border-neutral-600"
                          }`}
                        >
                          <div className="font-serif text-lg text-white">{p.label}</div>
                          <div className="text-xs text-neutral-400 mt-1">{p.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-neutral-400 mb-3 block">Drinks package</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {DRINK_PACKAGES.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => update("drinks_package", p.label)}
                          data-testid={`drinks-pkg-${p.id}`}
                          className={`text-left p-4 border transition-colors ${
                            data.drinks_package === p.label
                              ? "border-[#D4AF37] bg-[#D4AF37]/5"
                              : "border-neutral-800 hover:border-neutral-600"
                          }`}
                        >
                          <div className="font-serif text-lg text-white">{p.label}</div>
                          <div className="text-xs text-neutral-400 mt-1">{p.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6" data-testid="step-datetime">
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-neutral-400">Date</Label>
                    <Input
                      type="date"
                      data-testid="date-input"
                      min={new Date().toISOString().split("T")[0]}
                      value={data.event_date ? format(data.event_date, "yyyy-MM-dd") : ""}
                      onChange={(e) => update("event_date", e.target.value ? new Date(e.target.value + "T00:00:00") : null)}
                      className="mt-3 bg-transparent border-0 border-b border-neutral-800 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-[#D4AF37] h-auto [color-scheme:dark]"
                    />
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-neutral-400">Start time</Label>
                    <Input
                      type="time"
                      data-testid="time-input"
                      value={data.event_time}
                      onChange={(e) => update("event_time", e.target.value)}
                      className="mt-3 bg-transparent border-0 border-b border-neutral-800 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-[#D4AF37] h-auto [color-scheme:dark]"
                    />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-5" data-testid="step-details">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-xs tracking-[0.2em] uppercase text-neutral-400">Full name</Label>
                      <Input
                        data-testid="full-name-input"
                        value={data.full_name}
                        onChange={(e) => update("full_name", e.target.value)}
                        className="mt-3 bg-transparent border-0 border-b border-neutral-800 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-[#D4AF37] h-auto"
                      />
                    </div>
                    <div>
                      <Label className="text-xs tracking-[0.2em] uppercase text-neutral-400">Phone</Label>
                      <Input
                        data-testid="phone-input"
                        value={data.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        className="mt-3 bg-transparent border-0 border-b border-neutral-800 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-[#D4AF37] h-auto"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-neutral-400">Email</Label>
                    <Input
                      type="email"
                      data-testid="email-input"
                      value={data.email}
                      onChange={(e) => update("email", e.target.value)}
                      className="mt-3 bg-transparent border-0 border-b border-neutral-800 rounded-none px-0 py-3 text-white focus-visible:ring-0 focus-visible:border-[#D4AF37] h-auto"
                    />
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-neutral-400">Notes (optional)</Label>
                    <Textarea
                      data-testid="notes-input"
                      value={data.special_requests}
                      onChange={(e) => update("special_requests", e.target.value)}
                      placeholder="Themes, dietary needs, music preferences…"
                      className="mt-3 bg-transparent border border-neutral-800 rounded-none text-white focus-visible:ring-0 focus-visible:border-[#D4AF37] placeholder:text-neutral-600"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {done && (
            <div className="mt-10 text-center" data-testid="booking-success">
              <div className="mx-auto w-16 h-16 border border-[#D4AF37] flex items-center justify-center">
                <Check className="text-[#D4AF37]" />
              </div>
              <p className="mt-6 font-serif text-2xl text-white">Your enquiry is with us.</p>
              <p className="mt-3 text-neutral-400 text-sm max-w-md mx-auto">
                A member of our events team will reach out shortly to confirm the details and walk you through the room.
              </p>
              <Button
                onClick={() => { onOpenChange(false); setTimeout(reset, 300); }}
                data-testid="booking-close-btn"
                className="mt-8 bg-[#D4AF37] text-black hover:bg-[#C5A028] rounded-none px-8 py-4 tracking-[0.2em] uppercase text-xs"
              >
                Close
              </Button>
            </div>
          )}

          {!done && (
            <div className="mt-10 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                data-testid="booking-back-btn"
                className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-neutral-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={14} /> Back
              </button>
              {step < 3 ? (
                <Button
                  onClick={() => canNext() && setStep((s) => s + 1)}
                  disabled={!canNext()}
                  data-testid="booking-next-btn"
                  className="bg-[#D4AF37] text-black hover:bg-[#C5A028] rounded-none px-8 py-4 tracking-[0.2em] uppercase text-xs disabled:opacity-40"
                >
                  Continue <ArrowRight size={14} className="ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  disabled={!canNext() || submitting}
                  data-testid="booking-submit-btn"
                  className="bg-[#D4AF37] text-black hover:bg-[#C5A028] rounded-none px-8 py-4 tracking-[0.2em] uppercase text-xs disabled:opacity-40"
                >
                  {submitting ? "Sending…" : "Submit Enquiry"}
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
